<template>
  <div class="change-logs-list-page-component">
    <VdPage title="Version history" subtitle="See the changes introduced by a version" :loading="loading">
      <vd-empty v-if="!loading && sortedVersions.length === 0" title="No versions found">
        <p>No versions are available for this game.</p>
      </vd-empty>
      <div v-else class="versions-list">
        <div class="versions-header version-row">
          <span>Version</span>
          <span>Published by</span>
          <span class="versions-header__date">Published at</span>
        </div>
        <component
          :is="baseVersion ? 'RouterLink' : 'div'"
          v-for="{ version, baseVersion } in versionsWithBase"
          :key="version.id"
          :to="baseVersion ? changelogRoute(version, baseVersion) : undefined"
          class="version-row"
          :class="{ 'version-row--disabled': !baseVersion }"
        >
          <div class="version-info">
            <VersionLabel :version="version" />
            <span class="version-author">{{ version.publishedBy }}</span>
            <span class="version-date">
              {{ version.publishedDate ? formatDate(version.publishedDate) : "" }}
            </span>
          </div>
          <VdIcon v-if="baseVersion" store="fa" name="chevron-right" class="chevron-icon" />
        </component>
      </div>
    </VdPage>
  </div>
</template>

<script lang="ts">
import { VdPage } from "@voodoo.io/voodoo-ui";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { format, parseISO } from "date-fns";
import { computed, defineComponent, onMounted } from "vue";

import useVersions, { type Version } from "@/hooks/useVersions";
import links from "@/router/links";
import VersionLabel from "./VersionLabel.vue";

export default defineComponent({
  name: "VersionHistoryListPage",

  components: {
    VdPage,
    VdIcon,
    VersionLabel,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { versions, loading, fetchVersions } = useVersions(props.gameId);

    onMounted(fetchVersions);

    const filteredAndSortedVersions = computed<Version[]>(() =>
      versions.value
        ?.filter((version) => version.historySeq !== null && version.historySeq > 0)
        .sort((a, b) => (b.historySeq ?? 0) - (a.historySeq ?? 0))
    );

    const versionsWithBase = computed(() =>
      filteredAndSortedVersions.value.map((version, index) => ({
        version,
        baseVersion: filteredAndSortedVersions.value[index + 1] ?? null,
      }))
    );

    const formatDate = (date: string) => format(parseISO(date), "MMM d, yyyy HH:mm");

    const changelogRoute = (version: Version, baseVersion: Version) => ({
      name: links.games.versionHistoryDetail,
      params: {
        baseVersionId: baseVersion.id,
        targetVersionId: version.id,
      },
    });

    return {
      loading,
      sortedVersions: filteredAndSortedVersions,
      versionsWithBase,
      formatDate,
      changelogRoute,
    };
  },
});
</script>

<style lang="scss" scoped>
.change-logs-list-page-component {
  .versions-list {
    display: flex;
    flex-direction: column;
    gap: 0;

    @extend %card;
    overflow: hidden;
  }

  .version-row {
    display: grid;
    grid-template-columns: 4fr 1fr 120px 20px;
    align-items: center;
    gap: size("S");
    padding: size("S") size("M");
    border-bottom: 1px solid var(--vd-input-border-color);
    text-decoration: none;

    &:last-child {
      border-bottom: none;
    }

    &:not(&--disabled, .versions-header):hover {
      background-color: var(--vd-color-blue-light-9);

      .chevron-icon {
        opacity: 1;
        transform: translateX(2px);
      }
    }

    &--disabled {
      cursor: default;
      opacity: 0.5;
    }

    .version-info {
      display: contents;
    }

    .version-author,
    .version-date {
      font-size: font("S");
      color: var(--vd-base-placeholder-color);
    }

    .chevron-icon {
      color: var(--vd-base-placeholder-color);
      font-size: font("S");
      opacity: 0.4;
      transition: opacity 0.15s ease, transform 0.15s ease;
      flex-shrink: 0;
    }
  }

  .versions-header {
    border-bottom: 2px solid var(--vd-input-border-color);
    background-color: var(--vd-color-neutral-light-5);
    font-size: font("S");
    font-weight: 600;
    color: var(--vd-base-primary-color);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}
</style>
